import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const STATUS_STYLE = {
  pending:   { bg: '#fef3c7', color: '#e67e22' },
  completed: { bg: '#e6f4ea', color: '#27ae60' },
  abnormal:  { bg: '#fce8e6', color: '#c5221f' },
};

const EMPTY = {
  patientId: '', doctorId: '', testName: '', testDate: '',
  result: '', normalRange: '', status: 'pending', notes: '',
};

const inp = { className: 'form-control' };

function F({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export default function HospitalLabReports({ role }) {
  const [patients, setPatients]   = useState([]);
  const [doctors, setDoctors]     = useState([]);
  const [reports, setReports]     = useState([]);
  const [selectedPat, setSelectedPat] = useState(null);
  const [patSearch, setPatSearch] = useState('');
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY);
  const [editId, setEditId]       = useState(null);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');

  useEffect(() => {
    hospitalApi.getDoctors().then(d => { if (d) setDoctors(d.filter(doc => doc.status === 'active')); });
    hospitalApi.getPatients().then(d => { if (d) setPatients(d); });
    hospitalApi.getLabReports().then(d => { if (d) setReports(d); });
  }, []);

  const loadReports = useCallback((patId) => {
    if (patId) {
      hospitalApi.getLabReports(patId).then(d => { if (d) setReports(d); });
    } else {
      hospitalApi.getLabReports().then(d => { if (d) setReports(d); });
    }
  }, []);

  const handleSelectPat = (p) => {
    setSelectedPat(p.id);
    setPatSearch(p.name);
    loadReports(p.id);
    setForm(f => ({ ...f, patientId: String(p.id) }));
  };

  const resetForm = () => { setForm(EMPTY); setEditId(null); setErr(''); };

  const startEdit = (r) => {
    setForm({
      patientId: String(r.patient_id || ''), doctorId: String(r.doctor_id || ''),
      testName: r.test_name || '', testDate: r.test_date || '',
      result: r.result || '', normalRange: r.normal_range || '',
      status: r.status || 'pending', notes: r.notes || '',
    });
    setEditId(r.id);
    setShowForm(true);
    setErr('');
  };

  const handleSave = async () => {
    if (!form.patientId || !form.testName) { setErr('Patient and Test Name are required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form, patientId: parseInt(form.patientId), doctorId: parseInt(form.doctorId) || undefined };
      if (editId) await hospitalApi.updateLabReport(editId, payload);
      else await hospitalApi.createLabReport(payload);
      loadReports(selectedPat);
      setShowForm(false);
      resetForm();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lab report?')) return;
    await hospitalApi.deleteLabReport(id);
    loadReports(selectedPat);
  };

  const filtPats = patients.filter(p =>
    !patSearch || p.name?.toLowerCase().includes(patSearch.toLowerCase()) || p.phone?.includes(patSearch)
  );

  return (
    <div>
      {/* Patient search */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <label className="form-label">Filter by Patient (optional)</label>
          <input className="form-control" placeholder="Search patient…"
            value={patSearch}
            onChange={e => { setPatSearch(e.target.value); setSelectedPat(null); setForm(f => ({ ...f, patientId: '' })); if (!e.target.value) loadReports(null); }} />
          {patSearch && !selectedPat && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
              border: '1px solid #e8eaed', borderRadius: 6, maxHeight: 160, overflowY: 'auto', zIndex: 10,
              boxShadow: '0 4px 12px rgba(0,0,0,.12)' }}>
              {filtPats.slice(0, 8).map(p => (
                <div key={p.id} onClick={() => handleSelectPat(p)}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f3f4' }}
                  onMouseEnter={e => e.currentTarget.style.background = LIGHT}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  {p.name} — {p.phone || 'No phone'}
                </div>
              ))}
              {filtPats.length === 0 && <div style={{ padding: 10, color: '#9aa0a6', fontSize: 13 }}>No patients found</div>}
            </div>
          )}
        </div>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(!showForm); }}>
          {showForm ? '✕ Close Form' : '+ Add Lab Report'}
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div className="card-title">{editId ? 'Edit Lab Report' : 'Add Lab Report'}</div>
          </div>
          <div className="card-body">
            {err && <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠️ {err}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              <F label="Patient *">
                <select {...inp} value={form.patientId} onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))}>
                  <option value="">Select Patient</option>
                  {patients.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                </select>
              </F>
              <F label="Doctor">
                <select {...inp} value={form.doctorId} onChange={e => setForm(f => ({ ...f, doctorId: e.target.value }))}>
                  <option value="">Select Doctor</option>
                  {doctors.map(d => <option key={d.id} value={String(d.id)}>Dr. {d.name}</option>)}
                </select>
              </F>
              <F label="Test Name *">
                <input {...inp} value={form.testName} placeholder="e.g. Complete Blood Count"
                  onChange={e => setForm(f => ({ ...f, testName: e.target.value }))} />
              </F>
              <F label="Test Date">
                <input {...inp} type="date" value={form.testDate}
                  onChange={e => setForm(f => ({ ...f, testDate: e.target.value }))} />
              </F>
              <F label="Normal Range">
                <input {...inp} value={form.normalRange} placeholder="e.g. 70-100 mg/dL"
                  onChange={e => setForm(f => ({ ...f, normalRange: e.target.value }))} />
              </F>
              <F label="Status">
                <select {...inp} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="abnormal">Abnormal</option>
                </select>
              </F>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Result</label>
                <textarea {...inp} rows={2} value={form.result}
                  onChange={e => setForm(f => ({ ...f, result: e.target.value }))} style={{ resize: 'vertical' }} />
              </div>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Notes</label>
                <textarea {...inp} rows={2} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? '💾 Update' : '➕ Add Report'}
              </button>
              <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
                background: '#fff', cursor: 'pointer', fontSize: 13 }}
                onClick={() => { setShowForm(false); resetForm(); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Reports list */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            🧪 Lab Reports {selectedPat ? `— ${patSearch}` : '(All Patients)'}
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {reports.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9aa0a6' }}>No lab reports found</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: LIGHT }}>
                  {['Patient', 'Test Name', 'Date', 'Doctor', 'Normal Range', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.map((r, i) => {
                  const sc = STATUS_STYLE[r.status] || { bg: '#f1f3f4', color: '#5f6368' };
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: DARK }}>{r.patient_name || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>{r.test_name}</td>
                      <td style={{ padding: '10px 12px', color: '#5f6368' }}>{r.test_date || '—'}</td>
                      <td style={{ padding: '10px 12px', color: '#5f6368' }}>{r.doctor_name ? `Dr. ${r.doctor_name}` : '—'}</td>
                      <td style={{ padding: '10px 12px', color: '#5f6368', fontSize: 11 }}>{r.normal_range || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ background: sc.bg, color: sc.color,
                          borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button style={{ fontSize: 11, padding: '3px 8px', background: LIGHT,
                            color: DARK, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => startEdit(r)}>Edit</button>
                          <button style={{ fontSize: 11, padding: '3px 8px', background: '#fce8e6',
                            color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => handleDelete(r.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
